import React, {
  RefObject,
  useMemo,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import dayjs, {
  ManipulateType,
  OpUnitType,
} from 'dayjs';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Button from 'components/Button';
import CalendarPicker from 'core/components/DateTimePicker/base/DatePicker';
import {
  Dayjs,
} from 'utils/dayjs';
import {
  isEmptyString,
} from 'utils/misc';

type Props = {
  className?: string;
  containerClassName?: string;
  value?: Dayjs | string | Date | null;
  onChange: (value: Dayjs | undefined) => void;
  onBlur?: () => void;
  showDateButtons?: boolean;
  disableFuture?: boolean;
  disablePast?: boolean;
  accumulationUnit?: OpUnitType;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  disabled?: boolean;
  inputRef?: RefObject<HTMLInputElement>;
  inputFormat?: string;
  inputProps?: object;
  placeholder?: string;
  disabledDays?: Parameters<typeof CalendarPicker>[0]['disabledDays'];
  enableYearNavigation?: boolean;
  disableNavigation?: boolean;
  numberOfMonths?: number;
  fromYear?: number;
  toYear?: number;
  fromMonth?: Date;
  toMonth?: Date;
  fromDate?: Date;
  toDate?: Date;
  translations?: Parameters<typeof CalendarPicker>[0]['translations'];
  popoverPaperClassName?: string;
  popoverContentClassName?: string;
  calendarClassName?: string;
};

function toDayjsValue(value?: Dayjs | string | Date | null) {
  return isNil(value) || isEmptyString(value)
    ? undefined
    : dayjs(value);
}

export default function DatePicker({
  className,
  containerClassName,
  value,
  onChange,
  onBlur,
  showDateButtons = true,
  disablePast,
  disableFuture,
  accumulationUnit = 'day',
  disabled = false,
  minDate,
  maxDate,
  inputRef,
  inputFormat = 'MM/DD/YYYY',
  placeholder = 'Select date',
  disabledDays,
  enableYearNavigation,
  disableNavigation,
  numberOfMonths,
  fromYear,
  toYear,
  fromMonth,
  toMonth,
  fromDate,
  toDate,
  translations,
  popoverPaperClassName,
  popoverContentClassName,
  calendarClassName,
}: Props) {
  const currentValue = toDayjsValue(value);

  const nextButtonDate = useMemo(() => (
    currentValue?.isValid()
      ? currentValue.endOf(accumulationUnit).add(1, 'day').startOf('day')
      : undefined
  ), [currentValue, accumulationUnit]);

  const prevButtonDate = useMemo(() => (
    currentValue?.isValid()
      ? currentValue.startOf(accumulationUnit).subtract(1, accumulationUnit as ManipulateType)
      : undefined
  ), [currentValue, accumulationUnit]);

  const handleDateChange = (date: Date | undefined) => {
    onChange(isNil(date) ? undefined : dayjs(date));
  };

  return (
    <div className={clsx('flex justify-around items-center', containerClassName)}>
      {showDateButtons && (
        <Button
          className="!p-1 !rounded-r-none !border-r-0"
          type="button"
          variant="outline"
          onClick={() => {
            if (!isNil(prevButtonDate)) {
              onChange(prevButtonDate);
            }
          }}
        >
          <ChevronLeftIcon color="action" fontSize="small" />
        </Button>
      )}
      <CalendarPicker
        value={currentValue?.toDate() ?? null}
        onChange={handleDateChange}
        onBlur={onBlur}
        disabled={disabled}
        inputFormat={inputFormat}
        placeholder={placeholder}
        inputRef={inputRef}
        disabledDays={disabledDays}
        disablePast={disablePast}
        disableFuture={disableFuture}
        minDate={minDate}
        maxDate={maxDate}
        enableYearNavigation={enableYearNavigation}
        disableNavigation={disableNavigation}
        numberOfMonths={numberOfMonths}
        fromYear={fromYear}
        toYear={toYear}
        fromMonth={fromMonth}
        toMonth={toMonth}
        fromDate={fromDate}
        toDate={toDate}
        translations={translations}
        popoverPaperClassName={popoverPaperClassName}
        popoverContentClassName={popoverContentClassName}
        calendarClassName={calendarClassName}
        className={clsx('populate-date-picker', className)}
      />
      {showDateButtons && (
        <Button
          className="!p-1 !rounded-l-none !border-l-0"
          type="button"
          variant="outline"
          onClick={() => {
            if (!isNil(nextButtonDate)) {
              onChange(nextButtonDate);
            }
          }}
        >
          <ChevronRightIcon color="action" fontSize="small" />
        </Button>
      )}
    </div>
  );
}
